import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import BN from 'bignumber.js';
import Button from '@material-ui/core/Button';
import { Input } from '../../../../../../../components/form';
import stylesParent from '../../../styles/frame/frame.module.scss';
import styles from '../../../styles/stake/stake-settings.module.scss';
import Tooltip from '../../../../../../../components/tooltip';
import Header from '../../../../../../../components/Stake/header';
import PercentageButtons from '../../../../../../../components/Stake/percentage-buttons';
import { formatToken } from '../../../../../../../helpers/token-helper';
import { useGetTheme } from '../../../../../../../hooks/useGetTheme';
import * as bcubeStakeActions from '../../../../../../../actions/bcube-stake-actions';
import BcubeIcon from '../../../../../../../assets/images/BCUBE.svg';
import { toPowerOf } from '../../../../../../../blockchain/config';
import { BCUBE } from '../../../../../../../constants/blockchain-constants';

const StakeSettings = () => {
  const dispatch = useDispatch();
  const colors = useGetTheme();

  const [amount, setAmount] = useState(new BN(10000).times(toPowerOf).toString(10));
  const [inputValue, setInputValue] = useState(amount);

  useEffect(() => {
    setInputValue(+new BN(amount).div(toPowerOf).toFixed(2, BN.ROUND_DOWN));
  }, [amount]);

  const balance = useSelector((state) => state.token.bcubeBalance);

  const { stakeBalance, unstakedBalance, apr } = useSelector((state) => state.BCUBEstake.stake);

  const isNotFirstFrame = [stakeBalance, unstakedBalance].some((value) => value !== '0');
  const onClickBack = isNotFirstFrame ? () => dispatch(bcubeStakeActions.stakeReset()) : null;

  const estimatedDailyRewards = useMemo(() => {
    if (!apr) {
      return null;
    }
    return new BN(amount)
      .div(toPowerOf)
      .times(apr / 100)
      .div(365)
      .toFixed(0, BN.ROUND_DOWN);
  }, [amount, apr]);

  const handleAmountChange = (value) => {
    setAmount(value.amount);
  };

  return (
    <>
      <Header icon={BcubeIcon} title={`${BCUBE}-Staking`} onClickBack={onClickBack} />
      <div className={stylesParent.cardContent} style={{ color: colors.text }}>
        <div>
          <div className={stylesParent.cardTextRow}>
            <div className={stylesParent.cardTextRowLabel}>Your balance:</div>
            <div style={{ color: colors.text }} className={stylesParent.cardTextRowValue}>
              {formatToken(balance)} {BCUBE}
            </div>
          </div>
          <div className={stylesParent.cardTextRow}>
            <div className={stylesParent.cardTextRowLabel}>Current Total Stake:</div>
            <div className={stylesParent.cardTextRowValue}>
              {formatToken(stakeBalance)} {BCUBE}
            </div>
          </div>
        </div>
        <div className={styles.body}>
          <div className={styles.bodyTitle}>Stake Amount</div>
          <div className={styles.inputRow}>
            <span className={styles.inputRowLabel} style={{ color: colors.text }}>
              {BCUBE}
            </span>
            <div className={styles.inputRowContainer}>
              <Input
                value={inputValue}
                onChange={(e) =>
                  handleAmountChange({
                    amount: new BN(e.target.value || '0').times(toPowerOf).toString(10),
                  })
                }
                type='number'
              />
              <span className={styles.inputRowDesc}>Min. Stake: 1 {BCUBE}</span>
              <div className={styles.percentageContainer}>
                <PercentageButtons balance={balance} onClick={handleAmountChange} />
              </div>
            </div>
          </div>
          <div className={stylesParent.tooltipsContainer}>
            <div className={stylesParent.tooltipItem}>
              <Tooltip
                label='Estimated APR:'
                text={
                  <div className={stylesParent.tooltipText}>
                    Annual Percentage Reward (APR) refers to the annual percentage rate at which
                    your Staking assets earn Rewards on a yearly basis.
                    <br />
                    Click to learn more
                  </div>
                }
                value={`${apr}%`}
              />
            </div>
            <div className={stylesParent.tooltipItem}>
              <Tooltip
                label={
                  <>
                    Estimated
                    <br />
                    Daily Rewards:
                  </>
                }
                value={`${estimatedDailyRewards} ${BCUBE}`}
              />
            </div>
          </div>
          <div className={stylesParent.cardFooterControls}>
            <Button
              onClick={() => dispatch(bcubeStakeActions.stakeDetails({ amount }))}
              variant='contained'
              color='secondary'
              className={stylesParent.cardFooterControlsButton}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StakeSettings;
